import React, { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { TOAST_TYPES } from '@constants/constants';
import { setToastMessage } from '@stores/App/slice';
import { useLazyFetchSelfServeMMPConfigQuery } from '@api/selfServe';
import FormField, { FormFieldI } from '../../../../../components/Form/FormField';
import { RadioButtonUncheckedOutlined, RadioButtonCheckedOutlined } from '@mui/icons-material';

interface AdjustAccountFormProps {
  newGameData: any;
  handleUpdateNewGameData: any;
  onValidityChange?: (isValid: boolean) => void;
  disabled?: boolean;
}

const AdjustAccountForm: React.FC<AdjustAccountFormProps> = ({
  newGameData,
  handleUpdateNewGameData,
  onValidityChange,
  disabled = false,
}) => {
  const dispatch = useDispatch();

  const [accountName, setAccountName] = useState<string | null>(newGameData?.accountName || null);
  const [iapFormFields, setIapFormFields] = useState<FormFieldI | null>(null);
  const [iaaFormFields, setIaaFormFields] = useState<FormFieldI | null>(null);

  const [fetchSelfServeMMPConfigQuery] = useLazyFetchSelfServeMMPConfigQuery();

  // Hook for fetching the Appsflyer options.
  useEffect(() => {
    fetchDataAccessProviderConfigs();
  }, []);

  // Hook for validating accountName and iapMargin when changed.
  useEffect(() => {
    if (newGameData?.accountName && newGameData?.iapMargin && newGameData?.iaaIdList?.length > 0) {
      onValidityChange?.(true);
    }
  }, [newGameData]);

  const fetchDataAccessProviderConfigs = async () => {
    try {
      const { data } = await fetchSelfServeMMPConfigQuery().unwrap();
      if (data?.ADJUST?.iapInformation) {
        const iapFormField: FormFieldI = {
          type: 'select',
          label: 'IAP Margin',
          name: 'iapMargin',
          options:
            data.ADJUST.iapInformation.map((item: any) => ({
              label: `${(Number(item.value) * 100).toFixed(0)}%`,
              value: item.value,
            })) || [],
          required: true,
        };
        setIapFormFields(iapFormField);
      }
      if (data?.ADJUST?.iaaInformation) {
        const iaaFormField: FormFieldI = {
          type: 'multi-select',
          label: 'Ad Revenue Sources',
          name: 'iaaIdList',
          options:
            data.ADJUST.iaaInformation.map((item: any) => ({
              label: `${item.name}`,
              value: item.id,
            })) || [],
          required: true,
        };
        setIaaFormFields(iaaFormField);
      }
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message || null,
        }),
      );
    }
  };

  return (
    <Stack gap={'1.5rem'}>
      <Box>
        <TextField
          id="new-game-account-name-text-field"
          label="Adjust Account Name"
          placeholder="Adjust Account Name"
          variant="outlined"
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            handleUpdateNewGameData('accountName', e.target.value);
            setAccountName(e.target.value);
          }}
          value={accountName}
          sx={{
            width: '100%',
          }}
          disabled={disabled}
        />
      </Box>
      {iapFormFields && (
        <Box>
          <FormField
            field={iapFormFields}
            formValues={newGameData}
            handleInputChange={handleUpdateNewGameData}
            loading={false}
            disabled={disabled}
          />
        </Box>
      )}
      {iaaFormFields && (
        <Box>
          <FormField
            field={iaaFormFields}
            formValues={newGameData}
            handleInputChange={handleUpdateNewGameData}
            loading={false}
            disabled={disabled}
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography fontSize="0.85rem" fontWeight={700} sx={{ color: '#162936' }}>
          Monthly Refresh
        </Typography>
        <Box sx={{ display: 'flex', gap: '1rem' }}>
          {['Yes', 'No'].map((label, i) => {
            const value = i === 0;
            return (
              <FormControlLabel
                key={label}
                sx={{
                  width: 'fit-content',
                  borderRadius: '0.5rem',
                  mx: 0,
                  '& .MuiFormControlLabel-label': {
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    color: '#162936',
                  },
                }}
                label={label}
                control={
                  <Checkbox
                    checked={newGameData?.refreshRequired === value}
                    onChange={() => handleUpdateNewGameData('refreshRequired', value)}
                    icon={<RadioButtonUncheckedOutlined />}
                    checkedIcon={<RadioButtonCheckedOutlined />}
                    disabled={disabled}
                  />
                }
              />
            );
          })}
        </Box>
      </Box>
    </Stack>
  );
};

export default AdjustAccountForm;
